<template>
  <div v-if="execution" class="p-p-4">
    <Card>
      <template #title>
        <div class="flex justify-content-between align-items-center">
          <span class="space">Execution Detail</span>
          <Tag :value="execution.status" :severity="getStatusSeverity(execution.status)" />
        </div>
      </template>
      <template #subtitle>
        <span>ID: {{ execution.id }}</span>
      </template>
      <template #content>
        <div class="grid">
          <div class="col-12 md:col-6">
            <p><strong>Created At:</strong> {{ new Date(execution.created_at).toLocaleString() }}</p>
            <p><strong>Page Range:</strong> {{ execution.page_range }}</p>
          </div>
          <div class="col-12 md:col-6">
             <p><strong>Book ID:</strong> {{ execution.book_id }}</p>
             <p v-if="book"><strong>Book Title:</strong> {{ book.title }}</p>
             <p v-if="book"><strong>Book Identifier:</strong> {{ book.identifier }}</p>
          </div>
        </div>

        <div class="p-mt-4">
          <strong>Selected Pages:</strong>
          <div class="p-d-flex p-flex-wrap p-mt-2">
            <Chip 
              v-for="page in execution.selected_pages" 
              :key="page" 
              :label="String(page)" 
              class="p-mr-2 p-mb-2 clickable space" 
              @click="showPageDetail(page)"
              :class="{ 'active-chip': activePage === page }"
            />
          </div>
        </div>

        <div v-if="pageDetailHtml" class="p-mt-4">
            <Panel :header="`Page ${activePage} - Details`" toggleable>
                <div class="detail-container">
                    <div class="html-view">
                        <div v-html="pageDetailHtml" ref="htmlContainer"></div>
                    </div>
                    <div class="image-view">
                        <img v-if="pageImageUrl" :src="pageImageUrl" alt="Page Image" class="page-image" />
                    </div>
                </div>
            </Panel>
        </div>

        <div class="p-mt-4">
            <Button icon="pi pi-code" label="Show Raw Text" class="p-button-text p-button-sm" @click="toggleTextOutput" />
            <Button icon="pi pi-sitemap" label="Show Metadata" class="p-button-text p-button-sm p-ml-2" @click="toggleMetadataOutput" />
        </div>

        <transition name="p-toggleable-content">
          <div v-show="showTextOutput" class="p-mt-2">
            <Panel header="Raw Text Output" toggleable collapsed>
               <pre>{{ execution.text_output || 'No text output generated.' }}</pre>
            </Panel>
          </div>
        </transition>

        <transition name="p-toggleable-content">
            <div v-show="showMetadataOutput" class="p-mt-2">
                <Panel header="Execution Metadata" toggleable collapsed>
                    <pre>{{ execution.metadata_output }}</pre>
                </Panel>
            </div>
        </transition>

      </template>
      <template #footer>
        <Button label="Back to Book" icon="pi pi-arrow-left" @click="goBack" class="p-button-secondary" />&nbsp;
        <Button 
          label="Download LaTeX" 
          icon="pi pi-download" 
          @click="downloadLatex" 
          class="p-ml-2" 
          :disabled="!execution.metadata_output || !execution.metadata_output.latex_page" 
        />&nbsp;
        <Button 
          label="Download Package" 
          icon="pi pi-box" 
          @click="downloadPackage" 
          class="p-ml-2" 
          :disabled="execution.status !== 'COMPLETED'" 
        />&nbsp;
        <Button 
          label="Stampa Intervallo" 
          icon="pi pi-print" 
          @click="printPages" 
          class="p-button-info p-ml-2" 
          :disabled="!execution.selected_pages || execution.selected_pages.length === 0" 
        />&nbsp;
        <Button 
          label="Rerun" 
          icon="pi pi-refresh" 
          @click="rerunExecution" 
          class="p-button-warning p-ml-2" 
          v-if="execution.status === 'FAILED'" 
        />
      </template>
    </Card>
  </div>
  <div v-else>
    <p>Loading execution details...</p>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import api from '../services/api';
import Card from 'primevue/card';
import Tag from 'primevue/tag';
import Panel from 'primevue/panel';
import Button from 'primevue/button';
import Chip from 'primevue/chip';

const route = useRoute();
const router = useRouter();
const toast = useToast();

const execution = ref(null);
const book = ref(null);
const executionId = route.params.executionId;
const showMetadataOutput = ref(false);
const showTextOutput = ref(false);
const pageDetailHtml = ref(null);
const activePage = ref(null);
const htmlContainer = ref(null);
const pageImageUrl = ref(null);
let pollingInterval = null;

const fetchExecution = async () => {
  try {
    const response = await api.get(`/executions/${executionId}`);
    const newExecutionData = response.data;

    if (execution.value && execution.value.status !== newExecutionData.status) {
      toast.add({ 
        severity: 'info', 
        summary: 'Status Updated', 
        detail: `Execution status changed to ${newExecutionData.status}`,
        life: 5000 
      });
    }

    execution.value = newExecutionData;

    if (execution.value.book_id && !book.value) {
      fetchBookDetails(execution.value.book_id);
    }

    // If status is terminal, stop polling
    if (['COMPLETED', 'FAILED'].includes(execution.value.status.toUpperCase())) {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
      }
    }

  } catch (error) {
    console.error('Failed to fetch execution details:', error);
    if (pollingInterval) {
      clearInterval(pollingInterval);
      pollingInterval = null;
    }
  }
};

const startPolling = () => {
    if (pollingInterval) {
        clearInterval(pollingInterval);
    }
    if (execution.value && !['COMPLETED', 'FAILED'].includes(execution.value.status.toUpperCase())) {
        pollingInterval = setInterval(fetchExecution, 5000); // Poll every 5 seconds
    }
};

const fetchBookDetails = async (bookId) => {
  try {
    const response = await api.get(`/books/${bookId}`);
    book.value = response.data;
  } catch (error) {
    console.error('Failed to fetch book details:', error);
  }
};

const rerunExecution = async () => {
  try {
    await api.post(`/executions/${executionId}/rerun`);
    toast.add({ severity: 'success', summary: 'Success', detail: 'Execution is being rerun.', life: 3000 });
    await fetchExecution(); // Refresh execution data
    startPolling();
  } catch (error) {
    console.error('Failed to rerun execution:', error);
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to rerun execution.', life: 3000 });
  }
};

onMounted(async () => {
  await fetchExecution();
  startPolling();
});

onUnmounted(() => {
  if (pollingInterval) {
    clearInterval(pollingInterval);
  }
});

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(() => {
    // Optional: show a success message
    console.log('Copied to clipboard');
  }).catch(err => {
    console.error('Failed to copy: ', err);
  });
};

const attachCopyListeners = () => {
  if (!htmlContainer.value) return;

  htmlContainer.value.querySelectorAll('.math-action-btn').forEach(button => {
    button.addEventListener('click', () => {
      const content = button.getAttribute('data-content');
      copyToClipboard(content);
    });
  });
};

const processAndRenderHtml = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // 1. Set mathml elements to display: inline
  doc.querySelectorAll('mathml').forEach(el => {
    el.style.display = 'inline';
  });

  // 2. Wrap math-block and math-inline spans and add copy buttons
  doc.querySelectorAll('span.math-block, span.math-inline').forEach((span, index) => {
    const latex = span.querySelector('latex')?.textContent || '';
    const mathml = span.querySelector('mathml')?.innerHTML || '';
    const asciimath = span.querySelector('asciimath')?.textContent || '';

    const container = document.createElement('div');
    container.className = 'math-container';

    const actions = document.createElement('div');
    actions.className = 'math-actions';

    actions.innerHTML = `
      <button class="math-action-btn" data-content='${latex.replace(/'/g, "&apos;")}'>LaTeX</button>
      <button class="math-action-btn" data-content='${mathml.replace(/'/g, "&apos;")}'>MathML</button>
      <button class="math-action-btn" data-content='${asciimath.replace(/'/g, "&apos;")}'>AsciiMath</button>
    `;

    container.appendChild(actions);
    span.parentNode.insertBefore(container, span);
    container.appendChild(span);
  });

  pageDetailHtml.value = doc.body.innerHTML;

  nextTick(() => {
      attachCopyListeners();
  });
};

const showPageDetail = async (pageNumber) => {
  if (activePage.value === pageNumber) {
    pageDetailHtml.value = null;
    activePage.value = null;
    pageImageUrl.value = null;
    return;
  }

  try {
    const response = await api.get(`/books/${route.params.bookId}/pages/${pageNumber}`);
    const pageData = response.data;
    let htmlContent = '';

    // Set image URL
    pageImageUrl.value = pageData.image_path ? `${api.defaults.baseURL}/${pageData.image_path}` : null;

    if (pageData.json_data) {
      if (pageData.json_data.html) {
        htmlContent = pageData.json_data.html;
      } else if (pageData.json_data.data && pageData.json_data.data[0] && pageData.json_data.data[0].value) {
        htmlContent = pageData.json_data.data[0].value;
      }
    }

    if (htmlContent) {
        processAndRenderHtml(htmlContent);
    } else {
        pageDetailHtml.value = '<p>This page has not been processed or has no HTML content.</p>';
    }

    activePage.value = pageNumber;

  } catch (error) {
    console.error(`Failed to fetch details for page ${pageNumber}:`, error);
    pageDetailHtml.value = `<p>An error occurred while fetching details for page ${pageNumber}.</p>`;
    activePage.value = pageNumber;
  }
};

const toggleMetadataOutput = () => {
    showMetadataOutput.value = !showMetadataOutput.value;
};

const toggleTextOutput = () => {
    showTextOutput.value = !showTextOutput.value;
};

const getStatusSeverity = (status) => {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'PENDING':
      return 'warning';
    case 'PROCESSING':
      return 'info';
    case 'FAILED':
      return 'danger';
    default:
      return 'info';
  }
};

const goBack = () => {
  router.push({ name: 'BookDetail', params: { id: route.params.bookId } });
};

const sanitizeFileName = (name) => {
  // Sostituisce spazi, virgole e altri caratteri non validi con underscore
  return name.replace(/[,\s/]/g, '_').replace(/[^a-zA-Z0-9_.-]/g, '');
};

const downloadLatex = () => {
  if (!execution.value || !execution.value.metadata_output || !execution.value.metadata_output.latex_page) {
    console.error("No LaTeX content available to download.");
    return;
  }

  const latexContent = execution.value.metadata_output.latex_page;
  const blob = new Blob([latexContent], { type: 'application/x-latex;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  const fileName = execution.value.page_range 
    ? `execution_${sanitizeFileName(execution.value.page_range)}.tex`
    : `execution_${execution.value.id}.tex`;

  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const downloadPackage = async () => {
  if (!execution.value || !book.value) {
    console.error("Execution or book details not loaded.");
    return;
  }

  try {
    const response = await api.get(`/executions/${execution.value.id}/package`, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], { type: 'application/zip' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const bookIdentifier = book.value.identifier;
    const pageRange = execution.value.page_range 
      ? sanitizeFileName(execution.value.page_range)
      : execution.value.id;

    const fileName = `${bookIdentifier}_${pageRange}.zip`;

    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Failed to download execution package:", error);
  }
};

const printPages = async () => {
  if (!execution.value) {
    console.error("Execution details not loaded.");
    return;
  }

  try {
    const response = await api.get(`/executions/${execution.value.id}/print`, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    
    // Open the PDF in a new window for printing
    const printWindow = window.open(url, '_blank');
    
    // Clean up the blob URL after some time
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 10000);

    toast.add({ 
      severity: 'success', 
      summary: 'Success', 
      detail: 'PDF opened for printing', 
      life: 3000 
    });

  } catch (error) {
    console.error("Failed to generate print PDF:", error);
    toast.add({ 
      severity: 'error', 
      summary: 'Error', 
      detail: 'Failed to generate PDF for printing', 
      life: 3000 
    });
  }
};
</script>

<style scoped>
.space {
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
}
pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  background-color: #f4f4f4;
  padding: 1rem;
  border-radius: 6px;
}
.clickable {
  cursor: pointer;
}
.clickable:hover {
  opacity: 0.8;
}
.active-chip {
  background-color: var(--primary-color);
  color: var(--primary-color-text);
}

.detail-container {
  display: flex;
  gap: 1rem;
}

.html-view, .image-view {
  flex: 1;
  min-width: 0; /* Prevents flex items from overflowing */
}

.page-image {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

:deep(.math-container) {
  display: inline-block; /* Adjust size to content */
  position: relative;
  border: 1px solid transparent; /* Start with transparent border */
  margin: 4px;
  padding: 8px;
  padding-top: 28px; /* Make space for buttons */
  border-radius: 6px;
  transition: background-color 0.3s, border-color 0.3s, box-shadow 0.3s;
}

:deep(.math-container:hover) {
  background-color: rgba(0, 0, 0, 0.03);
  border-color: #e0e0e0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

:deep(.math-actions) {
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  gap: 4px;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
}

:deep(.math-container:hover .math-actions) {
  opacity: 1;
  visibility: visible;
}

:deep(.math-action-btn) {
  background-color: #f0f0f0;
  border: 1px solid #ccc;
  padding: 2px 6px;
  font-size: 10px;
  border-radius: 3px;
  cursor: pointer;
}
:deep(.math-action-btn:hover) {
  background-color: #e0e0e0;
}
</style>
